import { makeStyles } from '@material-ui/core/styles'
import { statTileStyles } from 'lib/styles/stat-tile'
import { CALLOUT_BACKGROUND } from 'lib/form-field-styles'
import { PGA_COACH_ERROR_TEXT } from 'lib/styles/pga-coach-pill-colors'

// The details page chrome (header, container width, section cards) comes from
// the coach settings page: `PageHeader` + `settings.styles` `contentContainer`
// and `SettingsSection`. The list page and upgrade view use the Students list
// container (full width). Only Virtual Coaching specifics live here.
export const useStyles = makeStyles(theme => ({
  upgradeContent: {
    boxSizing: 'border-box',
    maxWidth: '100%',
    padding: theme.spacing(3, 4.5, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 1, 2),
    },
  },
  upgradeTableShell: {
    marginTop: theme.spacing(2),
  },
  statusMeta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    '& .MuiSvgIcon-root, & .material-symbol': { fontSize: 18 },
  },
  statusMetaOverdue: {
    color: PGA_COACH_ERROR_TEXT,
  },
  // Icon-only header action, same as the student page header actions.
  compactButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(2),
  },
  // A tile that opens something; hover and focus come from ButtonBase.
  statTileButton: {
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  // Same flat tinted tiles as Coach Analytics and the student details stats.
  ...(({ statCard, statLabel, statValue }) => ({
    statTile: statCard,
    statTileLabel: statLabel,
    statTileValue: statValue,
  }))(statTileStyles(theme)),
  // Padded body inside a `SettingsSection` card.
  sectionBody: {
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  // Several golfer videos: a responsive grid of thumbnails that open a dialog.
  videoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  // A plain button reset; hover and focus are the browser/app defaults.
  videoTile: {
    display: 'block',
    width: '100%',
    padding: 0,
    border: 0,
    background: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
  videoTileLabel: { marginTop: theme.spacing(0.75) },
  videoNav: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 36,
    padding: theme.spacing(0, 3, 2),
  },
  // Keeps Next on the right when there is no Previous button.
  videoNavNext: { marginLeft: 'auto' },
  mediaVideo: {
    display: 'block',
    width: '100%',
    maxHeight: 360,
    borderRadius: 8,
    backgroundColor: '#172024',
  },
  mediaActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  // Answers can run to several sentences, so each one is a full-width block.
  contextList: {
    display: 'flex',
    flexDirection: 'column',
  },
  contextItem: {
    padding: theme.spacing(2, 0),
    '&:first-child': { paddingTop: 0 },
    '&:last-child': { paddingBottom: 0 },
    '& + &': { borderTop: '1px solid #E0E0E0' },
  },
  contextValue: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  buttonLabel: { textTransform: 'none' },
  formFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  video: {
    display: 'block',
    width: '100%',
    maxHeight: 480,
    borderRadius: 8,
    backgroundColor: '#172024',
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  notes: {
    whiteSpace: 'pre-wrap',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
  },
  helper: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  // The dashed upload area and "Choose" button from the golfer's video upload
  // form (`virtual-coaching-student/upload-form.js`, shared VideoFilePicker).
  dropZone: {
    border: '2px dashed #E0E0E0',
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: '#FAFBFC',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    // The shared picker's button is title-cased by the theme; keep sentence case.
    '& button, & button > span:first-child': { textTransform: 'none' },
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#E8EFFF',
  },
  pickerError: { marginTop: theme.spacing(1.5) },
  // The selected-file row from the golfer's upload form (iconTile, fileItem...).
  fileList: {
    listStyle: 'none',
    margin: theme.spacing(2, 0),
    padding: 0,
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  iconTile: {
    flexShrink: 0,
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: CALLOUT_BACKGROUND,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileMeta: { flex: 1, minWidth: 0 },
  fileName: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#172024',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileStatus: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
  fileStatusError: { color: theme.palette.error.main },
  fileActions: { display: 'flex', gap: theme.spacing(0.5), flexShrink: 0 },
  progress: {
    marginTop: theme.spacing(0.75),
    height: 6,
    borderRadius: 3,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
}))

export default useStyles
